import {
  autoUpdate,
  computePosition,
  flip,
  hide,
  offset,
  shift,
} from '@floating-ui/dom'
import * as Dialog from '@radix-ui/react-dialog'
import { VisuallyHidden } from '@radix-ui/react-visually-hidden'
import { type Content } from '@tiptap/react'
import type { TCollabThread } from '@tiptap-pro/provider'
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react'
import { type User } from '@opallabs/concepts'
import { getScrollableParent } from '@opallabs/lambda'
import { useTextEditor } from '../../../TextEditor'
import { Thread } from '../../../Thread'
import { ThreadContextProvider } from '../../../Thread/useThread'
import { ThreadCommentData, ThreadData } from '../../hooks/useThreads'
import { useThreadAnchor } from './useThreadAnchor'

export type ThreadPopoverProps = {
  thread: TCollabThread<ThreadData, ThreadCommentData>
  user: User
  onClose: () => void
  commentsPending: boolean
}

export const ThreadPopover: React.FC<ThreadPopoverProps> = ({
  thread,
  user,
  commentsPending,
  onClose,
}) => {
  const { editor, editorElement } = useTextEditor()
  const anchor = useThreadAnchor({ thread, viewElement: editor.view.dom })
  const [threadRef, setThreadRef] = useState<HTMLDivElement | null>(null)

  // Adds handlers to thread decorations to scroll into view if needed
  // when they are clicked on
  useEffect(() => {
    if (!anchor.contextElement) {
      return
    }

    const scrollPadding = 25
    const scrollParent = getScrollableParent('y', anchor.contextElement)

    const scrollHandler = () => {
      const anchorBounds = anchor.getBoundingClientRect()
      const scrollBounds = scrollParent.getBoundingClientRect()

      const isTopOccluded = anchorBounds.top < scrollBounds.top
      const isBottomOccluded = anchorBounds.bottom > scrollBounds.bottom

      if (isTopOccluded || isBottomOccluded) {
        // `scrollIntoView` does not work properly for document content,
        // so we need to use `scrollBy` for now
        scrollParent.scrollBy({
          behavior: 'smooth',
          top: isTopOccluded
            ? anchorBounds.top - scrollBounds.top - scrollPadding
            : anchorBounds.bottom - scrollBounds.bottom + scrollPadding,
        })
      }
    }

    scrollHandler()
    anchor.elements.forEach((element) =>
      element.addEventListener('click', scrollHandler)
    )
    return () => {
      anchor.elements.forEach((element) =>
        element.removeEventListener('click', scrollHandler)
      )
    }
  }, [anchor])

  useLayoutEffect(() => {
    if (!anchor.contextElement || !threadRef) {
      return
    }

    const cleanup = autoUpdate(anchor, threadRef, async () => {
      const { x, y, middlewareData } = await computePosition(
        anchor,
        threadRef,
        {
          placement: 'right',
          middleware: [
            offset(({ rects }) => ({
              mainAxis: 10,
              // For comments constrained to one line, prefer offsetting
              // the popover to mimic "-end" placement
              crossAxis:
                rects.reference.height < 20 ? rects.floating.height / 2 : 0,
            })),
            flip({
              crossAxis: false,
              flipAlignment: true,
            }),
            shift(),
            hide({
              boundary: editorElement ?? undefined,
            }),
          ],
        }
      )

      threadRef.style.left = `${x}px`
      threadRef.style.top = `${y}px`
      threadRef.style.visibility = middlewareData.hide?.referenceHidden
        ? 'hidden'
        : 'visible'
    })

    return () => {
      cleanup()
    }
  }, [anchor, threadRef, editorElement])

  const commentData: ThreadCommentData = useMemo(
    () => ({
      author_id: user.uuid,
      userName: user.name,
      userAvatar: user.asset?.url,
      pending: commentsPending,
      isUserEdit: false,
    }),
    [user, commentsPending]
  )

  const handleSubmitComment = useCallback(
    (input: Content) => {
      editor.commands.createComment({
        threadId: thread.id,
        content: input,
        data: commentData,
      })
    },
    [editor, thread, commentData]
  )

  const handleDeleteThread = useCallback(() => {
    editor.commands.removeThread({
      id: thread.id,
      deleteThread: true,
    })

    onClose()
  }, [thread, editor, onClose])

  const handleResolveThread = useCallback(() => {
    editor.commands.updateThread({
      id: thread.id,
      data: {
        ...thread.data,
        resolvedBy: {
          id: user.uuid,
          name: user.name,
        },
      },
    })

    editor.commands.resolveThread({
      id: thread.id,
    })

    onClose()
  }, [editor.commands, thread.id, thread.data, user.uuid, user.name, onClose])

  const handleEditComment = useCallback(
    (id: string, content: Content) => {
      editor.commands.updateComment({
        threadId: thread.id,
        id,
        content,
        data: { ...commentData, isUserEdit: true },
      })
    },
    [editor, thread, commentData]
  )

  const handleDeleteComment = useCallback(
    (id: string) => {
      if (thread.comments.length === 1) {
        handleDeleteThread()
        return
      }
      editor.commands.removeComment({
        threadId: thread.id,
        id,
      })
    },
    [editor, thread, handleDeleteThread]
  )

  return (
    <ThreadContextProvider
      onSubmitComment={handleSubmitComment}
      onDeleteThread={handleDeleteThread}
      onClose={onClose}
      onResolve={handleResolveThread}
      onEditComment={handleEditComment}
      onDeleteComment={handleDeleteComment}
    >
      <Dialog.Root
        open
        modal={false}
        onOpenChange={(open) => {
          if (!open) {
            onClose()
          }
        }}
      >
        <Dialog.Portal>
          <Dialog.Content
            onOpenAutoFocus={(e) => e.preventDefault()}
            /**
             * Re-enables scroll for non-modalled dialogs
             * @see https://github.com/radix-ui/primitives/issues/1159
             */
            onTouchMove={(e) => e.stopPropagation()}
            onWheel={(e) => e.stopPropagation()}
          >
            <VisuallyHidden>
              <Dialog.Description>
                Comment thread discussing selection of {anchor.textContent}
              </Dialog.Description>
            </VisuallyHidden>
            <Thread
              ref={(ref) => setThreadRef(ref)}
              thread={thread}
              user={user}
              resourceType={thread.data.resource_type}
              resourceId={thread.data.resource_id}
            />
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </ThreadContextProvider>
  )
}
